/**
 * Mirror of the capability AI assist wire contract
 * (`eduviaos-backend/app/domains/capability/{ai_schemas,ai_models,routes_ai}.py`).
 *
 * Phase F-finale-4 Slice 5. If a field is added/removed/renamed in the BE
 * schema, edit this file in the same PR (Iron Rule #11). Type drift between
 * FE + BE is a merge blocker.
 *
 * Enumerations here are also documented in TRD-17 §17.X (target_kind +
 * suggestion lifecycle + drawer states).
 */

import type { FormField } from "./forms.js";

// ─────────────────────────────────────────────────────────────────────────────
// Enums (stable — consumed by the diff drawer and TRD-17)
// ─────────────────────────────────────────────────────────────────────────────

/** Which level of the capability tree a suggestion targets. */
export type TargetKind = "funnel" | "stage" | "requirement" | "field";

/** Lifecycle of a single `CapabilityAISuggestion`. */
export type SuggestionStatus = "awaiting_review" | "partially_applied" | "rejected" | "expired";

/**
 * Polymorphic discriminator for a stage requirement. Mirrors the BE
 * `StageRequirement.kind` Literal in `payload_schemas/admission_stage.py`.
 */
export type StageRequirementKind =
  | "form_submission"
  | "document_upload"
  | "assessment_attempt"
  | "interview_completed"
  | "manual_check";

// ─────────────────────────────────────────────────────────────────────────────
// Composed domain shapes (mirror BE AdmissionStage / StageRequirement)
// ─────────────────────────────────────────────────────────────────────────────

/** One stage requirement as proposed by the LLM. Mirrors BE `StageRequirement`. */
export interface StageRequirementWire {
  key: string;
  kind: StageRequirementKind;
  label: string;
  config: Record<string, unknown>;
}

/** One admission stage as proposed by the LLM. Mirrors BE `AdmissionStage`. */
export interface AdmissionStageWire {
  key: string;
  label: string;
  order: number;
  is_terminal: boolean;
  terminal_outcome: "admitted" | "rejected" | null;
  color_hint?: string | null;
  auto_advance_days?: number | null;
  required_documents: string[];
  requirements: StageRequirementWire[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Per-node metadata + the four target-kind payload shapes
// ─────────────────────────────────────────────────────────────────────────────

/** Common per-node metadata. Mirrors BE `_SuggestionNodeMixin`. */
export interface SuggestionNode {
  /**
   * Stable dot-delimited address used as the diff drawer's React key AND the
   * apply selector (e.g. `stage:interview.req:assessment_attempt`). Populated
   * server-side — the LLM never invents these.
   */
  node_id: string;
  /** One-line LLM rationale rendered beneath the node label. */
  reason: string;
  /**
   * Optional module slug this node depends on. When set and the tenant has
   * not enabled it, the drawer renders a blocked badge and the apply route
   * rejects the node.
   */
  requires_module?: string | null;
}

/** L3 — one `StageRequirement` proposed by the LLM. */
export interface RequirementSuggestion extends SuggestionNode {
  parent_stage_key: string;
  requirement: StageRequirementWire;
}

/** L4 — one `FormField` proposed by the LLM. */
export interface FieldSuggestion extends SuggestionNode {
  form_slug: string;
  field: FormField;
}

/** L2 — one `AdmissionStage` (with optional bundled requirements). */
export interface StageSuggestion extends SuggestionNode {
  stage: AdmissionStageWire;
  requirements_for_stage: RequirementSuggestion[];
}

/** L1 — a whole funnel (ordered stages + optional bundled fields). */
export interface FunnelSuggestion extends SuggestionNode {
  funnel_name: string;
  stages: StageSuggestion[];
  fields: FieldSuggestion[];
}

/** The `parsed_payload` union — one shape per `target_kind`. */
export type SuggestionPayload =
  | FunnelSuggestion
  | StageSuggestion
  | RequirementSuggestion
  | FieldSuggestion;

// ─────────────────────────────────────────────────────────────────────────────
// Validation report (computed server-side post-parse)
// ─────────────────────────────────────────────────────────────────────────────

/** Per-node validity verdict. Mirrors BE `NodeValidation`. */
export interface NodeValidation {
  node_id: string;
  /** When `false`, the diff drawer disables this node's checkbox. */
  is_valid: boolean;
  /** Short explanation shown as a tooltip on a disabled checkbox. */
  reason?: string | null;
  /** Echo of the blocking module slug when the node is module-gated. */
  requires_module?: string | null;
}

// ─────────────────────────────────────────────────────────────────────────────
// Request / response envelopes (mirror routes_ai.py)
// ─────────────────────────────────────────────────────────────────────────────

/** PII-stripped prompt context. Mirrors BE `_PromptContextIn`. */
export interface CapabilityPromptContextWire {
  tenant_id: string;
  vertical: string;
  enabled_modules: string[];
  assessment_template_slugs: string[];
  existing_stages: Array<Record<string, unknown>>;
  available_form_slugs: string[];
  draft_version: number;
}

/** `POST /ai/capability/suggest` body. Mirrors BE `_SuggestRequest`. */
export interface SuggestRequest {
  target_kind: TargetKind;
  user_prompt: string;
  context: CapabilityPromptContextWire;
}

/** `POST /ai/capability/suggestions/{id}/apply` body. Mirrors BE `_ApplyRequest`. */
export interface ApplyRequest {
  accepted_node_ids: string[];
  draft_version: number;
}

/** Response for `/suggest` and `/suggestions/{id}`. Mirrors BE `_SuggestionOut`. */
export interface CapabilitySuggestion {
  id: string;
  tenant_id: string;
  target_kind: TargetKind;
  user_prompt: string;
  status: SuggestionStatus;
  parsed_payload: SuggestionPayload;
  validation_report: NodeValidation[];
  applied_node_ids: string[];
  draft_version_at_creation: number;
}

/** One skipped node in an apply response. */
export interface SkippedNode {
  node_id: string;
  reason: string;
}

/** Response for the apply endpoint. Mirrors BE `_ApplyResponseOut`. */
export interface ApplyResult {
  id: string;
  status: SuggestionStatus;
  applied_node_ids: string[];
  skipped_node_ids: SkippedNode[];
}

// ─────────────────────────────────────────────────────────────────────────────
// Prompt-input constraints (mirror BE Field validators — keep in sync)
// ─────────────────────────────────────────────────────────────────────────────

/** Minimum prompt length the BE accepts (`user_prompt` min_length). */
export const MIN_PROMPT_LENGTH = 12;
/** Maximum prompt length the BE accepts (`user_prompt` max_length). */
export const MAX_PROMPT_LENGTH = 500;

/** Hard caps from the BE schema (mirror `ai_schemas.py`). */
export const MAX_STAGES_PER_FUNNEL = 8;
export const MAX_REQUIREMENTS_PER_STAGE = 5;
export const MAX_FIELDS_PER_FUNNEL_SUGGESTION = 20;
